import React, { useState, useEffect } from 'react';
import { eventsAPI } from '../utils/api';
import EventCard from '../components/EventCard';
import { Search, Calendar, Filter, AlertCircle } from 'lucide-react';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterAndSortEvents();
  }, [events, searchTerm, locationFilter, specificDate, typeFilter]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching events...');
      const response = await eventsAPI.getAll();
      
      console.log('API Response:', response);
      
      // Handle different response formats
      let eventsData = [];
      
      if (response && response.data) {
        // Check if response.data is an array
        if (Array.isArray(response.data)) {
          eventsData = response.data;
        } 
        // Check if response.data.data exists (nested structure)
        else if (response.data.data && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }
        // Check if response.data.events exists
        else if (response.data.events && Array.isArray(response.data.events)) {
          eventsData = response.data.events;
        }
      } 
      // If response itself is an array
      else if (Array.isArray(response)) {
        eventsData = response;
      }
      
      console.log('Processed events data:', eventsData);
      setEvents(eventsData);
      
    } catch (error) {
      console.error('Error fetching events:', error);
      setError(error.message || 'Failed to load events. Please try again later.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortEvents = () => {
    if (!Array.isArray(events)) {
      setFilteredEvents([]);
      return;
    }

    let filtered = events.filter(event => {
      // Safely access properties with fallbacks
      const name = event.name || '';
      const description = event.description || '';
      const venue = event.venue || '';
      
      // Search filter
      const matchesSearch = !searchTerm || 
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        venue.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Location filter
      const matchesLocation = locationFilter === 'all' || 
        venue.toLowerCase().includes(locationFilter.toLowerCase());
      
      // Date filter
      let matchesDate = true;
      if (specificDate && event.date) {
        try {
          const eventDate = new Date(event.date);
          const selectedDate = new Date(specificDate);
          matchesDate = eventDate.toDateString() === selectedDate.toDateString();
        } catch (e) {
          console.error('Date parsing error:', e);
          matchesDate = false;
        }
      }
      
      // Type filter - simplified logic
      const matchesType = typeFilter === 'all' || 
        name.toLowerCase().includes(typeFilter.toLowerCase()) ||
        description.toLowerCase().includes(typeFilter.toLowerCase());
      
      return matchesSearch && matchesLocation && matchesDate && matchesType;
    });

    // Sort by date (earliest first for upcoming events)
    filtered.sort((a, b) => {
      try {
        return new Date(a.date) - new Date(b.date);
      } catch (e) {
        return 0;
      }
    });

    setFilteredEvents(filtered);
  };

  // Filter for upcoming events only
  const upcomingEvents = filteredEvents.filter(event => {
    try {
      return new Date(event.date) >= new Date();
    } catch (e) {
      return false;
    }
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="mb-8 sm:mb-12">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4 sm:mb-6">
          Discover Events
        </h1>
        <p className="text-lg sm:text-xl text-black mb-6 sm:mb-8">
          Find and register for amazing events near you
        </p>
        
        {/* Search and Filter */}
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
            <input
              type="text"
              placeholder="Search events or venues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10 sm:pl-12 h-12 sm:h-14 text-base sm:text-lg w-full"
              aria-label="Search events"
            />
          </div>
          
          {/* Filter Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="input-field h-12 sm:h-14 text-base sm:text-lg appearance-none"
              aria-label="Filter by location"
            >
              <option value="all">All Locations</option>
              <option value="mumbai">Mumbai</option>
              <option value="delhi">Delhi</option>
              <option value="bangalore">Bangalore</option>
              <option value="pune">Pune</option>
              <option value="hyderabad">Hyderabad</option>
            </select>
            
            <input
              type="date"
              value={specificDate}
              onChange={(e) => setSpecificDate(e.target.value)}
              className="input-field h-12 sm:h-14 text-base sm:text-lg w-full"
              placeholder="Select specific date"
              aria-label="Filter by date"
            />
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="input-field h-12 sm:h-14 text-base sm:text-lg appearance-none"
              aria-label="Filter by event type"
            >
              <option value="all">All Types</option>
              <option value="conference">Conference</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
              <option value="networking">Networking</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-1">
                Unable to Load Events
              </h3>
              <p className="text-red-700 mb-3">{error}</p>
              <button
                onClick={fetchEvents}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="card-premium rounded-2xl p-4 sm:p-6 animate-pulse">
              <div className="h-40 sm:h-48 bg-white/10 rounded-xl mb-4"></div>
              <div className="h-5 sm:h-6 bg-white/10 rounded mb-3"></div>
              <div className="h-4 bg-white/10 rounded mb-4"></div>
              <div className="space-y-2">
                <div className="h-3 sm:h-4 bg-white/10 rounded"></div>
                <div className="h-3 sm:h-4 bg-white/10 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Events Grid */}
          {upcomingEvents.length > 0 && (
            <div className="mb-12 sm:mb-16">
              <h2 className="text-2xl sm:text-3xl font-bold text-black mb-6 sm:mb-8 flex items-center">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-yellow-600" />
                <span>Upcoming Events ({upcomingEvents.length})</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                {upcomingEvents.map((event) => (
                  <EventCard key={event.eventId || event.id || Math.random()} event={event} />
                ))}
              </div>
            </div>
          )}

          {/* No Events State */}
          {upcomingEvents.length === 0 && !loading && !error && (
            <div className="text-center py-20">
              <Calendar className="w-20 h-20 text-gray-300 mx-auto mb-6" />
              <h3 className="text-2xl font-semibold text-black mb-4">
                {searchTerm || locationFilter !== 'all' || specificDate || typeFilter !== 'all'
                  ? 'No events found'
                  : 'No events available'}
              </h3>
              <p className="text-black text-lg mb-6">
                {searchTerm || locationFilter !== 'all' || specificDate || typeFilter !== 'all'
                  ? 'Try adjusting your search filters to see more events.'
                  : 'Check back later for exciting upcoming events.'}
              </p>
              {(searchTerm || locationFilter !== 'all' || specificDate || typeFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setLocationFilter('all');
                    setSpecificDate('');
                    setTypeFilter('all');
                  }}
                  className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Events;